import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';

import { deleteApi, ErrorCustom, get, post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type {
    AiConversation,
    AiConversationsListResponse
} from '@/components/composite/SparksAgent/types';

import { AI_CONVERSATION, AI_CONVERSATIONS } from '@/lib/routes/apiEndpoints';

const DEFAULT_LIMIT = 30;

export async function createAiConversation(): Promise<AiConversation> {
    const result = await post<{ data: AiConversation }>(AI_CONVERSATIONS, {});
    return (result as { data: AiConversation })?.data as AiConversation;
}

export async function deleteAiConversation(conversationId: string): Promise<void> {
    const endpoint = injectEndpointUrlParams(AI_CONVERSATION, {
        CONVERSATION_ID: conversationId
    });
    await deleteApi(endpoint);
}

type UseAiConversationsProps = {
    limit?: number;
    options?: SWRInfiniteConfiguration<AiConversationsListResponse, ErrorCustom>;
    shouldFetch?: boolean;
};

function getKey(
    pageIndex: number,
    previousPageData: AiConversationsListResponse | null,
    limit: number
) {
    if (previousPageData && !previousPageData.next_cursor) return null;

    const base = `${AI_CONVERSATIONS}?limit=${limit}`;
    if (pageIndex === 0) return base;
    if (previousPageData?.next_cursor) {
        return `${base}&cursor=${encodeURIComponent(previousPageData.next_cursor)}`;
    }
    return null;
}

export function useAiConversations({
    limit = DEFAULT_LIMIT,
    options = {},
    shouldFetch = true
}: UseAiConversationsProps = {}) {
    const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
        AiConversationsListResponse,
        ErrorCustom
    >(
        shouldFetch
            ? (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, limit)
            : null,
        async (url) => {
            const result = await get<{ data: AiConversationsListResponse }>(url);
            return result?.data as AiConversationsListResponse;
        },
        {
            revalidateOnFocus: false,
            revalidateIfStale: false,
            ...options
        }
    );

    const conversations: AiConversation[] = data ? data.flatMap((page) => page?.data ?? []) : [];
    const loadMore = () => setSize(size + 1);
    const hasMore = Boolean(data?.length && data[data.length - 1]?.next_cursor);

    return {
        conversations,
        data,
        error,
        isLoading,
        isLoadingMore: size > 0 && data && typeof data[size - 1] === 'undefined' && isValidating,
        loadMore,
        hasMore,
        mutate
    };
}
